// TenantUsersDialog — manage one tenant's users (ticket 205, Damien 2026-10-05).
// List, suspend / unsuspend, change role, send the normal password-reset email.
// No impersonation and no password set by hand. Owner and external seats are
// read-only here. The host page (SuperAdminPage) already gates on is_super_admin.
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { api } from '../api';
import { toast } from '../../hooks/use-toast';
import { errorMessage } from '../errors';
import { confirmDialog } from '@/components/dialogs/DialogHost';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

export const roleLabel = (r) => (r || '').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

const btn = 'text-[12px] font-semibold px-2.5 py-1.5 rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50';

export default function TenantUsersDialog({ tenant, onClose }) {
  const [items, setItems] = useState(null);
  const [roles, setRoles] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => api.get(`/super/tenants/${tenant.tenant_id}/users`)
    .then(({ data }) => { setItems(data.items || []); setRoles(data.roles || []); setError(null); })
    .catch((e) => setError(errorMessage(e, 'Could not load users'))), [tenant.tenant_id]);

  useEffect(() => { load(); }, [load]);

  const act = async (u, key, request, ok) => {
    setBusy(`${u.user_id}-${key}`);
    try {
      const { data } = await request();
      toast(ok(data));
      await load();
    } catch (e) {
      toast({ title: 'Action failed', description: errorMessage(e, 'Try again') });
    } finally {
      setBusy(null);
    }
  };

  const base = `/super/tenants/${tenant.tenant_id}/users`;

  const toggleSuspend = async (u) => {
    const suspending = u.status !== 'suspended';
    const confirmed = await confirmDialog({
      title: `${suspending ? 'Suspend' : 'Unsuspend'} ${u.name || u.email}?`,
      description: suspending
        ? 'They are signed out of this workspace on their next request. Their data is kept.'
        : 'They can sign in to this workspace again.',
      confirmText: suspending ? 'Suspend' : 'Unsuspend',
    });
    if (!confirmed) return;
    await act(u, 'status',
      () => api.patch(`${base}/${u.user_id}/status`, { status: suspending ? 'suspended' : 'active' }),
      () => ({ title: `${u.name || u.email} ${suspending ? 'suspended' : 'unsuspended'}` }));
  };

  const changeRole = async (u, role) => {
    if (role === u.role) return;
    const confirmed = await confirmDialog({
      title: `Change ${u.name || u.email} to ${roleLabel(role)}?`,
      description: `Currently ${roleLabel(u.role)}. Their access in this workspace changes immediately; the change is audit-logged.`,
      confirmText: 'Change role',
    });
    if (!confirmed) return;
    await act(u, 'role',
      () => api.patch(`${base}/${u.user_id}/role`, { role }),
      () => ({ title: `${u.name || u.email} is now ${roleLabel(role)}` }));
  };

  const sendReset = async (u) => {
    const confirmed = await confirmDialog({
      title: `Email a password reset to ${u.email}?`,
      description: 'They get the normal reset link by email. You never see or set their password.',
      confirmText: 'Send email',
    });
    if (!confirmed) return;
    await act(u, 'reset',
      () => api.post(`${base}/${u.user_id}/password-reset`),
      (d) => (d.email_sent
        ? { title: 'Reset email sent', description: d.email }
        : { title: 'Reset email not sent', description: 'FRONTEND_URL is not set on the server, so no link could be built.' }));
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0 gap-0" data-testid="tenant-users-dialog">
        <div className="px-6 pt-5 pb-3 border-b border-slate-100">
          <div className="text-[11px] uppercase tracking-widest font-bold" style={{ color: '#6BA82E' }}>Owner Only</div>
          <DialogTitle className="font-serif text-xl font-bold" style={{ color: '#070B5F' }}>Users — {tenant.name}</DialogTitle>
          <DialogDescription className="text-[12px] text-slate-500 mt-0.5">
            Every change is written to the audit log. Owner seats change through Transfer ownership; borrower and realtor seats take their rights from the deal.
          </DialogDescription>
        </div>

        <div className="px-6 py-4">
          {error && <div className="text-[13px] text-red-700" data-testid="tu-error">{error}</div>}
          {!error && items === null && <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 size={14} className="animate-spin" /> Loading…</div>}
          {items && items.length === 0 && <div className="text-sm text-slate-500" data-testid="tu-empty">Nobody has joined this workspace yet.</div>}
          {items && items.length > 0 && (
            <ul className="divide-y divide-slate-100">
              {items.map((u) => {
                const suspended = u.status === 'suspended';
                const rowBusy = !!busy && busy.startsWith(u.user_id);
                return (
                  <li key={u.user_id} className="py-3 flex flex-wrap items-center gap-x-4 gap-y-2" data-testid={`tu-row-${u.user_id}`}>
                    <div className="min-w-0 flex-1 basis-56">
                      <div className="text-[13px] font-semibold text-slate-800 truncate">
                        {u.name || u.email}
                        {suspended && <span className="ml-2 font-sans font-bold uppercase text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700" data-testid={`tu-suspended-${u.user_id}`}>Suspended</span>}
                      </div>
                      <div className="text-[12px] text-slate-500 truncate">{u.email}</div>
                    </div>
                    <div className="w-44">
                      {u.manageable ? (
                        <Select value={u.role} onValueChange={(r) => changeRole(u, r)} disabled={rowBusy}>
                          <SelectTrigger data-testid={`tu-role-${u.user_id}`} className="h-9 w-full bg-white text-[13px]"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {roles.map((r) => <SelectItem key={r} value={r}>{roleLabel(r)}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="text-[13px] text-slate-600" data-testid={`tu-role-${u.user_id}`}>{roleLabel(u.role)}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {u.role !== 'owner' && (
                        <button className={btn} disabled={rowBusy} onClick={() => toggleSuspend(u)} data-testid={`tu-suspend-${u.user_id}`}>
                          {suspended ? 'Unsuspend' : 'Suspend'}
                        </button>
                      )}
                      <button
                        className={btn}
                        disabled={rowBusy || !u.can_reset_password}
                        title={u.can_reset_password ? undefined : 'Signs in without a password'}
                        onClick={() => sendReset(u)}
                        data-testid={`tu-reset-${u.user_id}`}
                      >
                        Send reset email
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-100 flex justify-end">
          <button onClick={onClose} className="text-[13px] px-4 py-2 rounded-lg text-slate-500 hover:bg-slate-100" data-testid="tu-close">Close</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
